import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { API_BASE_URL, authFetchJson } from '../../apiClient.ts';
import { EmptyState, RecordSection } from '../../components/record/RecordPage.tsx';
import { formatDateTime } from '../../settings/mailIntegrations.ts';
import { getErrorMessage } from '../../utils/helperUtils.ts';

type MailStatus = 'ok' | 'not_connected' | 'reconnect_needed' | 'no_email';

type MailMessage = {
  id: string;
  mailbox: string;
  subject: string | null;
  from: { name: string | null; address: string | null } | null;
  recipients: string[];
  receivedAt: string | null;
  preview: string | null;
  webLink: string | null;
  direction: 'inbound' | 'outbound';
};

type Listing = { status: MailStatus; mailbox: string | null; messages: MailMessage[] };

// A message to a distribution list would otherwise fill the card with
// addresses. Show two, count the rest, and keep the full list on hover.
const Recipients = ({ addresses }: { addresses: string[] }) => {
  if (!addresses.length) return <>To unknown</>;
  if (addresses.length <= 3) return <>To {addresses.join(', ')}</>;
  return (
    <Tooltip title={addresses.join(', ')} placement="top-start">
      <span>
        To {addresses.slice(0, 2).join(', ')} and {addresses.length - 2} more
      </span>
    </Tooltip>
  );
};

type OwedItem = { item: string; due: string | null; source: string };

type Summary = {
  status: MailStatus;
  mailbox: string | null;
  messageCount: number;
  generatedAt: string | null;
  summary: {
    relationship: string;
    youOwe: OwedItem[];
    theyOwe: OwedItem[];
    openQuestions: string[];
    nextStep: string | null;
    lastTouch: string | null;
  } | null;
};

const statusHint: Record<Exclude<MailStatus, 'ok'>, string> = {
  not_connected:
    'Connect a mailbox in Settings to see the email you have exchanged with this contact.',
  reconnect_needed: 'Your mailbox needs to be reconnected in Settings before email can be read.',
  no_email: 'This contact has no email address on file, so there is nothing to look up.'
};

const OwedList = ({ title, items }: { title: string; items: OwedItem[] }) => (
  <Box>
    <Typography variant="subtitle2">{title}</Typography>
    {items.length ? (
      <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, my: 0.5 }}>
        {items.map((item, index) => (
          <Typography component="li" variant="body2" key={`${item.item}-${index}`}>
            {item.item}
            {item.due ? ` (due ${item.due})` : ''}
            {item.source ? (
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}
                · {item.source}
              </Typography>
            ) : null}
          </Typography>
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Nothing outstanding.
      </Typography>
    )}
  </Box>
);

// Live view of the mailbox: what was exchanged with this person, and on
// demand, what each side still owes. Nothing here is stored in the CRM.
export const ContactMailSection = ({ contactId }: { contactId: number }) => {
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summarising, setSummarising] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { json } = await authFetchJson(
        `${API_BASE_URL}/integrations/mail/contacts/${contactId}/messages`
      );
      setListing(json as Listing);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    setSummary(null);
    setSummaryError(null);
    void load();
  }, [load]);

  const summarise = async () => {
    setSummarising(true);
    setSummaryError(null);
    try {
      const { json } = await authFetchJson(
        `${API_BASE_URL}/integrations/mail/contacts/${contactId}/summary`,
        { method: 'POST' }
      );
      setSummary(json as Summary);
    } catch (err) {
      setSummaryError(getErrorMessage(err));
    } finally {
      setSummarising(false);
    }
  };

  const canSummarise = listing?.status === 'ok' && listing.messages.length > 0;

  return (
    <RecordSection
      title="Recent email"
      description={
        listing?.mailbox
          ? `From your connected mailbox${listing.mailbox.includes(',') ? 'es' : ''}, ${listing.mailbox}.`
          : undefined
      }
      action={
        canSummarise ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={
              summarising ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AutoAwesomeOutlinedIcon />
              )
            }
            disabled={summarising}
            onClick={summarise}
          >
            {summarising ? 'Reading' : "Summarize what's owed"}
          </Button>
        ) : undefined
      }
    >
      {summaryError ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {summaryError}
        </Alert>
      ) : null}

      {summary?.summary ? (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: 'primary.light',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="body2" sx={{ mb: 1.25 }}>
            {summary.summary.relationship}
          </Typography>
          <Stack spacing={1.25}>
            <OwedList title="You owe them" items={summary.summary.youOwe} />
            <OwedList title="They owe you" items={summary.summary.theyOwe} />
            {summary.summary.openQuestions.length ? (
              <Box>
                <Typography variant="subtitle2">Open questions</Typography>
                <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, my: 0.5 }}>
                  {summary.summary.openQuestions.map((q, index) => (
                    <Typography component="li" variant="body2" key={`${q}-${index}`}>
                      {q}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            ) : null}
            {summary.summary.nextStep ? (
              <Box>
                <Typography variant="subtitle2">Suggested next step</Typography>
                <Typography variant="body2">{summary.summary.nextStep}</Typography>
              </Box>
            ) : null}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Read {summary.messageCount} message{summary.messageCount === 1 ? '' : 's'}
            {summary.generatedAt ? ` at ${formatDateTime(summary.generatedAt)}` : ''}. Nothing from
            the summary is saved.
          </Typography>
        </Box>
      ) : summary && summary.status === 'ok' ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          No email to summarise.
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Checking your mailbox
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">Could not read email: {error}</Alert>
      ) : !listing || listing.status !== 'ok' ? (
        <Typography variant="body2" color="text.secondary">
          {statusHint[(listing?.status ?? 'not_connected') as Exclude<MailStatus, 'ok'>]}{' '}
          {listing?.status !== 'no_email' ? (
            <MuiLink component={RouterLink} to="/settings">
              Open Settings
            </MuiLink>
          ) : null}
        </Typography>
      ) : listing.messages.length === 0 ? (
        <EmptyState>No email with this contact in your connected mailbox.</EmptyState>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {listing.messages.map((message) => (
            <Box
              key={message.id}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 1 }}
            >
              <Box sx={{ color: 'text.secondary', mt: 0.35, lineHeight: 0 }}>
                {message.direction === 'inbound' ? (
                  <CallReceivedIcon fontSize="small" />
                ) : (
                  <CallMadeIcon fontSize="small" />
                )}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {message.subject || '(no subject)'}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={message.direction === 'inbound' ? 'Received' : 'Sent'}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {message.direction === 'inbound' ? (
                    `From ${message.from?.name || message.from?.address || 'unknown'}`
                  ) : (
                    <Recipients addresses={message.recipients} />
                  )}
                  {message.receivedAt ? ` · ${formatDateTime(message.receivedAt)}` : ''}
                  {listing.mailbox?.includes(',') ? ` · via ${message.mailbox}` : ''}
                </Typography>
                {message.preview ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {message.preview}
                  </Typography>
                ) : null}
              </Box>
              {message.webLink ? (
                <Button
                  size="small"
                  component="a"
                  href={message.webLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  sx={{ flexShrink: 0 }}
                >
                  Open
                </Button>
              ) : null}
            </Box>
          ))}
        </Stack>
      )}
    </RecordSection>
  );
};
